import React, { useContext, useEffect, useRef, useState } from 'react';
import { useSecurityMode, useSecurityRegistryVersion } from './SecurityModeContext';
import { Clause, RegisteredArea, SecuredFlag } from './types';
import { AuthenticatedUserContext } from '@components/auth/AuthenticatedUser';

const POSITION_STORAGE_KEY = 'security.overlayPosition';
const PANEL_WIDTH = 420;

const readStoredPosition = (): { x: number; y: number } => {
  try {
    const raw = localStorage.getItem(POSITION_STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (typeof parsed.x === 'number' && typeof parsed.y === 'number') return parsed;
    }
  } catch {
    // ignore
  }
  return { x: Math.max(8, window.innerWidth - PANEL_WIDTH - 8), y: 80 };
};

const SecurityOverlay: React.FC = () => {
  const { registry, enabled, setEnabled, selectedAreaId, selectAreaId, selectionVersion } = useSecurityMode();
  const auth = useContext(AuthenticatedUserContext);
  useSecurityRegistryVersion();
  const [open, setOpen] = useState(true);
  const [query, setQuery] = useState('');
  const selectedRowRef = useRef<HTMLDivElement | null>(null);
  const [position, setPosition] = useState(readStoredPosition);
  const dragOffsetRef = useRef<{ dx: number; dy: number } | null>(null);

  useEffect(() => {
    if (!selectedAreaId) return;
    if (!open) setOpen(true);
    selectedRowRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [selectedAreaId, selectionVersion]);

  useEffect(() => {
    try {
      localStorage.setItem(POSITION_STORAGE_KEY, JSON.stringify(position));
    } catch {
      // ignore
    }
  }, [position]);

  const handleHeaderMouseDown = (e: React.MouseEvent) => {
    if (e.button !== 0) return;
    if ((e.target as HTMLElement).closest('button')) return;
    e.preventDefault();
    dragOffsetRef.current = { dx: e.clientX - position.x, dy: e.clientY - position.y };
    const onMove = (ev: MouseEvent) => {
      if (!dragOffsetRef.current) return;
      const nx = Math.min(Math.max(0, ev.clientX - dragOffsetRef.current.dx), window.innerWidth - 40);
      const ny = Math.min(Math.max(0, ev.clientY - dragOffsetRef.current.dy), window.innerHeight - 40);
      setPosition({ x: nx, y: ny });
    };
    const onUp = () => {
      dragOffsetRef.current = null;
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  if (!enabled || !registry) return null;

  const flashStyle = `
    @keyframes security-flash-anim { 0% { background-color: rgba(255,200,0,0.4); } 100% { background-color: transparent; } }
    .security-flash { animation: security-flash-anim 1.2s ease-out; }
  `;

  const flags = registry.getFlags();
  const areas = registry.getAreas();
  const q = query.trim().toLowerCase();

  const matchesQuery = (text: string) => !q || text.toLowerCase().includes(q);
  const flagMatchesQuery = (f: SecuredFlag): boolean => {
    if (matchesQuery(f.id)) return true;
    if (f.metadata) {
      for (const c of f.metadata.clauses) {
        if (clauseMatchesQuery(c)) return true;
      }
    }
    return false;
  };
  const clauseMatchesQuery = (c: Clause): boolean => {
    if (c.type === 'permission') return c.anyOf.some(p => matchesQuery(p));
    if (c.type === 'feature') return c.anyOf.some(p => matchesQuery(p));
    if (c.type === 'module') return c.anyOf.some(m => matchesQuery(m.module));
    return matchesQuery(c.label ?? '');
  };
  const areaMatchesQuery = (a: RegisteredArea): boolean => {
    if (matchesQuery(a.id)) return true;
    if (a.showFlags.some(flagMatchesQuery)) return true;
    if (a.editableFlags.some(flagMatchesQuery)) return true;
    if (a.showInline.some(clauseMatchesQuery)) return true;
    if (a.editableInline.some(clauseMatchesQuery)) return true;
    return false;
  };

  const filteredFlags = flags.filter(flagMatchesQuery).sort((a, b) => a.id.localeCompare(b.id));
  const filteredAreas = areas.filter(areaMatchesQuery).sort((a, b) => a.id.localeCompare(b.id));
  const selectedArea = selectedAreaId ? areas.find(a => a.id === selectedAreaId) ?? null : null;

  const userPermissions = [...(auth?.loggedUser?.permissionTypes ?? [])].sort();
  const userFeatures = [...(auth?.loggedUser?.features ?? [])].sort();
  const filteredUserPermissions = userPermissions.filter(p => matchesQuery(p));
  const filteredUserFeatures = userFeatures.filter(f => matchesQuery(f));

  return (
    <>
      <style>{flashStyle}</style>
      <div
        className="fixed z-[9999] bg-white border-2 border-black shadow-2xl rounded-lg"
        style={{ left: position.x, top: position.y, width: open ? PANEL_WIDTH : 'auto', maxHeight: '85vh' }}
      >
      <div
        className="flex items-center justify-between p-2 border-b cursor-move select-none text-white"
        style={{ backgroundColor: 'var(--ringil3-brand-color, #FE5000)' }}
        onMouseDown={handleHeaderMouseDown}
      >
        <span className="font-bold text-sm tracking-wide">⋮⋮ SECURITY MODE</span>
        <div className="flex gap-1">
          <button
            type="button"
            className="px-2 py-0.5 text-xs rounded bg-white/20 hover:bg-white/30 text-white"
            onClick={() => setOpen(o => !o)}
          >
            {open ? '−' : '+'}
          </button>
          <button
            type="button"
            className="px-2 py-0.5 text-xs rounded bg-white text-red-700 hover:bg-red-100"
            onClick={() => setEnabled(false)}
          >
            off
          </button>
        </div>
      </div>
      {open && (
        <div className="overflow-y-auto p-2" style={{ maxHeight: 'calc(85vh - 50px)' }}>
          {selectedArea && (
            <Section title="Selected">
              <AreaRow area={selectedArea} isSelected={true} onSelect={selectAreaId} forceExpanded />
            </Section>
          )}
          <Legend />
          <input
            type="text"
            placeholder="Filter by permission / flag / clause…"
            className="w-full border rounded px-2 py-1 text-xs mb-2"
            value={query}
            onChange={e => setQuery(e.target.value)}
          />
          <Section title={`Areas (${filteredAreas.length}/${areas.length})`}>
            {filteredAreas.length === 0 && <div className="text-xs text-gray-500 italic">no areas registered yet</div>}
            {filteredAreas.map(a => (
              <AreaRow
                key={a.id}
                area={a}
                isSelected={selectedAreaId === a.id}
                onSelect={selectAreaId}
                rowRef={selectedAreaId === a.id ? selectedRowRef : undefined}
              />
            ))}
          </Section>
          <Section title={`Flags (${filteredFlags.length}/${flags.length})`}>
            {filteredFlags.length === 0 && <div className="text-xs text-gray-500 italic">no flags registered</div>}
            {filteredFlags.map(f => (
              <FlagRow key={f.id} flag={f} />
            ))}
          </Section>
          <GrantList title={`User permissions (${filteredUserPermissions.length}/${userPermissions.length})`} items={filteredUserPermissions} empty="no permissions" />
          <GrantList title={`User features (${filteredUserFeatures.length}/${userFeatures.length})`} items={filteredUserFeatures} empty="no features" />
        </div>
      )}
      </div>
    </>
  );
};

const Legend: React.FC = () => (
  <div className="border rounded p-2 mb-2 text-[11px] font-mono leading-tight bg-gray-50">
    <div className="font-bold mb-1">Legend</div>
    <div>
      <span className="inline-block w-3 h-3 border-2 border-dashed border-green-500 mr-1 align-middle" /> visible + editable (RW)
    </div>
    <div>
      <span className="inline-block w-3 h-3 border-2 border-dashed border-yellow-500 mr-1 align-middle" /> visible + read-only (RO)
    </div>
    <div>
      <span className="inline-block w-3 h-3 border-2 border-dashed border-gray-400 mr-1 align-middle" /> visible, display-only
    </div>
    <div>
      <span className="inline-block w-3 h-3 border-2 border-dashed border-red-500 mr-1 align-middle" /> hidden (ghost)
    </div>
    <div className="mt-1 text-gray-600">Click any chip on the page to expand its clauses.</div>
  </div>
);

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <CollapsibleSection title={title}>{children}</CollapsibleSection>
);

const CollapsibleSection: React.FC<{ title: string; defaultOpen?: boolean; children: React.ReactNode }> = ({ title, defaultOpen = true, children }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="mb-3">
      <button
        type="button"
        className="flex w-full items-center gap-1 text-xs font-bold mb-1 text-left"
        onClick={() => setOpen(o => !o)}
      >
        <span className="text-gray-500 w-3">{open ? '▾' : '▸'}</span>
        {title}
      </button>
      {open && children}
    </div>
  );
};

const GrantList: React.FC<{ title: string; items: string[]; empty: string }> = ({ title, items, empty }) => (
  <Section title={title}>
    {items.length === 0 ? (
      <div className="text-xs text-gray-500 italic">{empty}</div>
    ) : (
      <div className="text-[11px] font-mono leading-tight text-green-700">
        {items.map(p => (
          <div key={p}>✓ {p}</div>
        ))}
      </div>
    )}
  </Section>
);

const scrollPageToArea = (id: string) => {
  const el = document.querySelector(`[data-security-area="${CSS.escape(id)}"]`);
  if (el) {
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    el.classList.add('security-flash');
    setTimeout(() => el.classList.remove('security-flash'), 1200);
  }
};

interface AreaRowProps {
  area: RegisteredArea;
  isSelected: boolean;
  onSelect: (id: string | null) => void;
  rowRef?: React.RefObject<HTMLDivElement | null>;
  forceExpanded?: boolean;
}

const priorityHex = (visible: boolean, editable: boolean | null): string => {
  if (!visible) return '#ef4444';
  if (editable === null) return '#9ca3af';
  if (editable) return '#22c55e';
  return '#eab308';
};

const AreaRow: React.FC<AreaRowProps> = ({ area, isSelected, onSelect, rowRef, forceExpanded }) => {
  const [manualExpanded, setManualExpanded] = useState(false);
  const expanded = forceExpanded || isSelected || manualExpanded;
  const color = !area.visible
    ? 'text-red-700'
    : area.editable === null
      ? 'text-gray-600'
      : area.editable
        ? 'text-green-700'
        : 'text-yellow-700';
  const priority = priorityHex(area.visible, area.editable);
  const ringStyle = isSelected ? { boxShadow: `0 0 0 2px ${priority}` } : undefined;
  const rowBg = isSelected ? '' : 'bg-gray-50 hover:bg-gray-100';
  const rowStyle = isSelected ? { backgroundColor: `${priority}1a` } : undefined;
  return (
    <div ref={rowRef} className="border rounded mb-1 text-[11px] font-mono" style={ringStyle}>
      <div
        className={`flex items-center justify-between p-1 cursor-pointer ${rowBg}`}
        style={rowStyle}
        onClick={() => {
          onSelect(isSelected ? null : area.id);
          scrollPageToArea(area.id);
        }}
      >
        <span className={`text-left font-bold ${color}`}>{area.id}</span>
        <button
          type="button"
          className="px-1 text-gray-500"
          onClick={e => {
            e.stopPropagation();
            setManualExpanded(x => !x);
          }}
        >
          {expanded ? '−' : '+'}
        </button>
      </div>
      {expanded && (
        <div className="p-1 border-t">
          <RulesBlock title="show" flags={area.showFlags} inline={area.showInline} />
          {(area.editableFlags.length > 0 || area.editableInline.length > 0) && (
            <RulesBlock title="editable" flags={area.editableFlags} inline={area.editableInline} />
          )}
        </div>
      )}
    </div>
  );
};

const RulesBlock: React.FC<{ title: string; flags: SecuredFlag[]; inline: Clause[] }> = ({ title, flags, inline }) => (
  <div className="mb-1">
    <div className="text-gray-500">{title}:</div>
    {flags.map(f => (
      <div key={f.id} className={f.granted ? 'text-green-700' : 'text-red-700'}>
        <div className="pl-2">
          {f.granted ? '✓' : '✗'} flag: {f.id}
        </div>
        {f.metadata?.clauses?.map((c, i) => (
          <ClauseLine key={i} clause={c} />
        ))}
      </div>
    ))}
    {inline.map((c, i) => (
      <ClauseLine key={`inline-${i}`} clause={c} />
    ))}
  </div>
);

const FlagRow: React.FC<{ flag: SecuredFlag }> = ({ flag }) => {
  const [expanded, setExpanded] = useState(false);
  const color = flag.granted ? 'text-green-700' : 'text-red-700';
  return (
    <div className="border rounded mb-1 text-[11px] font-mono">
      <div className="flex items-center justify-between p-1 bg-gray-50">
        <span className={`font-bold ${color}`}>
          {flag.granted ? '✓' : '✗'} {flag.id}
        </span>
        <button type="button" className="px-1 text-gray-500" onClick={() => setExpanded(e => !e)}>
          {expanded ? '−' : '+'}
        </button>
      </div>
      {expanded && (
        <div className="p-1 border-t">
          {flag.metadata?.clauses?.map((c, i) => (
            <ClauseLine key={i} clause={c} />
          )) ?? <div className="text-gray-500 italic">no metadata</div>}
        </div>
      )}
    </div>
  );
};

const ClauseLine: React.FC<{ clause: Clause }> = ({ clause }) => {
  const color = clause.satisfied ? 'text-green-700' : 'text-red-700';
  const mark = clause.satisfied ? '✓' : '✗';
  let body: string;
  switch (clause.type) {
    case 'permission':
      body = `perm anyOf: ${clause.anyOf.join(', ')}`;
      break;
    case 'feature':
      body = `feat anyOf: ${clause.anyOf.join(', ')}`;
      break;
    case 'module':
      body = `module anyOf: ${clause.anyOf
        .map(m => (m.inherited == null ? m.module : `${m.module}(${m.inherited ? 'inherited' : 'owned'})`))
        .join(', ')}`;
      break;
    default:
      body = `${clause.type}: ${clause.label}`;
  }
  return (
    <div className={`pl-4 ${color}`}>
      {mark} {body}
    </div>
  );
};

export default SecurityOverlay;